/**
 * Écran de liste des aventures - Identique à Flutter ListScreen
 */

import React, {useEffect, useState, useCallback} from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  Platform,
  TextInput,
  RefreshControl,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useAuth} from '../contexts/AuthContext';
import adventureService from '../services/adventureService';
import {ListAdventure} from '../models/Adventure';
import AdventureCard from '../components/AdventureCard';
import {LightTheme} from '../theme/theme';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faRocket, faScroll, faDragon, faMagic, faCrown, faSprayCan, faCog} from '@fortawesome/free-solid-svg-icons';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Fonts} from '../config/fonts';

const ListScreen: React.FC = () => {
  const navigation = useNavigation();
  const {isLoggedIn, signOut} = useAuth();
  const insets = useSafeAreaInsets();
  const [adventures, setAdventures] = useState<ListAdventure[]>([]);
  const [allAdventures, setAllAdventures] = useState<ListAdventure[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{[key: string]: number}>({});
  const [isDownloading, setIsDownloading] = useState<{[key: string]: boolean}>({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (isLoggedIn) {
      loadAdventures();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedTheme, selectedDifficulty, allAdventures]);

  const loadAdventures = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      console.log('[ListScreen] Starting to load adventures from DB');
      // 1) Lecture brute depuis DB
      const rawDb = await adventureService.getLocalAdventures();
      console.log(`[ListScreen] Loaded ${rawDb.length} adventures from DB`);

      // 2) Tentative de chargement depuis API (si connecté)
      let rawApi: any[] = [];
      try {
        rawApi = await adventureService.loadAdventures();
        console.log(`[ListScreen] Successfully loaded ${rawApi.length} adventures from API`);
      } catch (apiError) {
        console.error('[ListScreen] Error loading adventures from API:', apiError);
        // On continue avec les aventures locales uniquement
      }

      // 3) Fusionner les aventures (logique identique à Flutter)
      const merged = await adventureService.mergeAdventures(rawDb, rawApi);
      console.log(`[ListScreen] Final merged list has ${merged.length} adventures`);

      setAllAdventures(merged);
      setAdventures(merged);

      if (merged.length === 0) {
        setErrorMessage(
          'Aucune aventure disponible.\nVeuillez réessayer plus tard.'
        );
      }
    } catch (error: any) {
      console.error('[ListScreen] Error in loadAdventures:', error);
      setErrorMessage(
        'Une erreur est survenue lors du chargement des aventures.\nVeuillez réessayer plus tard.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const applyFilters = () => {
    const filtered = allAdventures.filter((adv) => {
      // Filtre par recherche
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        if (
          !adv.name.toLowerCase().includes(query) &&
          !(adv.description?.toLowerCase().includes(query) ?? false) &&
          !adv.theme.toLowerCase().includes(query)
        ) {
          return false;
        }
      }

      // Filtre par thème
      if (selectedTheme && adv.theme !== selectedTheme) {
        return false;
      }

      // Filtre par difficulté
      if (selectedDifficulty && adv.difficulty !== selectedDifficulty) {
        return false;
      }

      return true;
    });

    setAdventures(filtered);
  };

  const availableThemes = Array.from(
    new Set(allAdventures.map((a) => a.theme).filter(Boolean))
  ).sort();

  const availableDifficulties = Array.from(
    new Set(allAdventures.map((a) => a.difficulty).filter(Boolean))
  ).sort();

  const handleDownloadOrUpdate = async (adventure: ListAdventure) => {
    setIsDownloading((prev) => ({...prev, [adventure.uniqueURI]: true}));
    setDownloadProgress((prev) => ({...prev, [adventure.uniqueURI]: 0}));

    try {
      await adventureService.downloadOrUpdateAdventure(
        adventure.uniqueURI,
        (received) => {
          if (adventure.contentSize > 0) {
            const progress = Math.min(received / adventure.contentSize, 1.0);
            setDownloadProgress((prev) => ({
              ...prev,
              [adventure.uniqueURI]: progress,
            }));
          }
        }
      );

      // Mettre à jour la liste après téléchargement
      await loadAdventures();
    } catch (error: any) {
      console.error('[ListScreen] Error downloading adventure:', error);
      // TODO: Afficher un message d'erreur
    } finally {
      setIsDownloading((prev) => ({...prev, [adventure.uniqueURI]: false}));
      setDownloadProgress((prev) => ({...prev, [adventure.uniqueURI]: 0}));
    }
  };

  const handlePlay = (adventure: ListAdventure) => {
    // Naviguer vers GameMenuScreen (menu de jeu avec slots)
    const parent = navigation.getParent();
    if (parent) {
      parent.navigate('GameMenu' as never, {
        adventureURI: adventure.uniqueURI,
        adventureName: adventure.name,
      } as never);
    } else {
      navigation.navigate('GameMenu' as never, {
        adventureURI: adventure.uniqueURI,
        adventureName: adventure.name,
      } as never);
    }
  };

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadAdventures();
  }, []);

  const renderAdventure = ({item}: {item: ListAdventure}) => (
    <AdventureCard
      adventure={item}
      onPlay={() => handlePlay(item)}
      onDownloadOrUpdate={handleDownloadOrUpdate}
      isDownloading={isDownloading[item.uniqueURI] || false}
      downloadProgress={downloadProgress[item.uniqueURI] || 0}
    />
  );

  if (loading && !refreshing) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
      </View>
    );
  }

  const handleSettings = () => {
    navigation.navigate('Settings' as never);
  };

  return (
    <View style={styles.container}>
      {/* Micro-header éditorial */}
      <View style={[styles.editorialHeader, {paddingTop: insets.top + 16}]}>
        <Text style={styles.editorialTitle}>L'AVENTURIER</Text>
        <TouchableOpacity
          style={styles.settingsIconButton}
          onPress={handleSettings}
          activeOpacity={0.7}>
          <FontAwesomeIcon icon={faCog} size={16} color="rgba(139, 115, 85, 0.6)" />
        </TouchableOpacity>
      </View>

      {/* Barre de recherche et filtres */}
      <View style={styles.filtersContainer}>
        <TextInput
          style={styles.searchInput}
          placeholder="Rechercher une aventure..."
          placeholderTextColor="#999"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />

        {/* Filtres de thème */}
        {availableThemes.length > 0 && (
          <View style={styles.filtersRow}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.filtersScrollContent}>
              <Text style={styles.filterLabel}>Thème:</Text>
              {availableThemes.map((themeName) => {
                const isSelected = selectedTheme === themeName;
                // Icône selon le thème (narratif)
                const themeIcon = themeName.toUpperCase().includes('SF') || themeName.toUpperCase().includes('SCIENCE')
                  ? faRocket
                  : themeName.toUpperCase().includes('FANTASY') || themeName.toUpperCase().includes('FANTASTIQUE')
                  ? faDragon
                  : themeName.toUpperCase().includes('MYSTERY') || themeName.toUpperCase().includes('MYSTÈRE')
                  ? faScroll
                  : themeName.toUpperCase().includes('MAGIC') || themeName.toUpperCase().includes('MAGIE')
                  ? faMagic
                  : themeName.toUpperCase().includes('ROYAL') || themeName.toUpperCase().includes('KING')
                  ? faCrown
                  : faSprayCan; // Par défaut
                
                return (
                  <TouchableOpacity
                    key={themeName}
                    style={[
                      styles.filterChip,
                      isSelected && styles.filterChipSelected,
                    ]}
                    onPress={() => {
                      setSelectedTheme(isSelected ? null : themeName);
                    }}>
                    <FontAwesomeIcon
                      icon={themeIcon}
                      size={14}
                      color={isSelected ? '#fff' : '#666'}
                      style={{marginRight: 6}}
                    />
                    <Text
                      style={[
                        styles.filterChipText,
                        isSelected && styles.filterChipTextSelected,
                      ]}>
                      {themeName}
                    </Text>
                  </TouchableOpacity>
                );
              })}
              {(selectedTheme || selectedDifficulty) && (
                <TouchableOpacity
                  style={styles.resetButton}
                  onPress={() => {
                    setSelectedTheme(null);
                    setSelectedDifficulty(null);
                  }}>
                  <Text style={styles.resetButtonText}>Réinitialiser</Text>
                </TouchableOpacity>
              )}
            </ScrollView>
          </View>
        )}
      </View>

      {errorMessage && adventures.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.errorText}>{errorMessage}</Text>
          <Text style={styles.emptyText}>
            Tirez vers le bas pour actualiser
          </Text>
        </View>
      ) : (
        <FlatList
          data={adventures}
          renderItem={renderAdventure}
          keyExtractor={(item) => item.uniqueURI}
          contentContainerStyle={styles.list}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyNarrativeText}>Une aventure vous attend…</Text>
              <Text style={styles.emptyText}>
                {searchQuery || selectedTheme || selectedDifficulty
                  ? 'Aucune aventure ne correspond à votre recherche'
                  : 'Aucune aventure disponible pour le moment'}
              </Text>
            </View>
          }
          ListFooterComponent={
            adventures.length > 0 ? (
              <View style={styles.footerContainer}>
                <Text style={styles.footerNarrativeText}>
                  D'autres aventures arrivent bientôt…
                </Text>
              </View>
            ) : null
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F3ED',
    minHeight: Platform.OS === 'web' ? '100vh' : undefined,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: LightTheme.colors.bg,
    minHeight: Platform.OS === 'web' ? '100vh' : undefined,
  },
  filtersContainer: {
    padding: 20,
    backgroundColor: LightTheme.colors.surface,
    borderBottomWidth: 0, // Pas de bordure
    paddingBottom: 24, // Plus d'espace en bas
  },
  searchInput: {
    backgroundColor: '#f8f8f8', // Fond clair, discret
    ...Platform.select({
      web: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 8,
      },
      default: {
        borderRadius: 10,
      },
    }),
    padding: 12,
    fontSize: 15,
    color: LightTheme.colors.text,
    marginBottom: 18, // Plus d'espace
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.06)', // Bordure très douce
  },
  filtersRow: {
    marginTop: 8,
  },
  filtersScrollContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#666',
    marginRight: 8,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 9,
    // Coins légèrement irréguliers (style papier)
    ...Platform.select({
      web: {
        borderTopLeftRadius: 10,
        borderTopRightRadius: 12,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 8,
      },
      default: {
        borderRadius: 12,
      },
    }),
    backgroundColor: '#F5F3ED', // Beige doux (cohérence avec jeu)
    marginRight: 10, // Plus d'espace
    borderWidth: 1,
    borderColor: '#D4C4B0', // Beige moyen, bordure douce
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)', // Ombre très douce
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.04,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
  },
  filterChipSelected: {
    backgroundColor: '#8B4513', // Brun narratif
    borderColor: '#6B3410',
  },
  filterChipText: {
    fontSize: 14,
    color: '#2C3E50',
    fontWeight: '600',
    fontFamily: Fonts.ui,
    letterSpacing: 0.3,
  },
  filterChipTextSelected: {
    color: '#E8E6E1',
  },
  resetButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginLeft: 8,
  },
  resetButtonText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '500',
  },
  list: {
    padding: LightTheme.spacing.lg,
    paddingTop: 20, // Plus d'espace en haut (respiration avec filtres)
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: LightTheme.spacing.xxxl,
  },
  emptyNarrativeText: {
    fontSize: 20,
    fontFamily: Fonts.title,
    fontWeight: '600',
    color: '#8B4513',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: 0.5,
  },
  emptyText: {
    fontSize: LightTheme.fontSize.ui,
    color: LightTheme.colors.textMuted,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  footerContainer: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  footerNarrativeText: {
    fontSize: 16,
    fontFamily: Fonts.title,
    color: '#8A8A8A',
    fontStyle: 'italic',
    letterSpacing: 0.3,
  },
  errorText: {
    fontSize: LightTheme.fontSize.ui,
    color: '#d32f2f',
    textAlign: 'center',
    marginBottom: 16,
  },
  editorialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingBottom: 12,
    backgroundColor: 'transparent',
  },
  editorialTitle: {
    fontSize: 16,
    fontFamily: Fonts.title,
    color: '#6B5B4A', // Légèrement plus foncé que le texte
    letterSpacing: 1,
    fontWeight: '400',
  },
  settingsIconButton: {
    padding: 8,
  },
  settingsMenuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    paddingTop: Platform.OS === 'web' ? 80 : 80,
    paddingRight: 24,
  },
  settingsMenu: {
    backgroundColor: '#F5F3ED',
    borderRadius: 12,
    paddingVertical: 8,
    minWidth: 180,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 4},
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 8,
      },
    }),
  },
  settingsMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  settingsMenuItemText: {
    fontSize: 15,
    color: '#2C2C2C',
    marginLeft: 12,
    fontFamily: Fonts.reading,
  },
  settingsMenuItemDanger: {
    color: '#D32F2F',
  },
  settingsMenuDivider: {
    height: 1,
    backgroundColor: '#D4C4B0',
    marginVertical: 4,
    opacity: 0.4,
  },
});

export default ListScreen;
